import { useEffect, useState, useCallback, memo, useRef } from "react";
import api from "../../api/api";
import Swal from "sweetalert2";
import styles from "./SubClientePage.module.scss";

// ─── Service ──────────────────────────────────────────────────────────────────

const subClienteService = {
  list:   ()           => api.get("/sub-clientes"),
  create: (payload)    => api.post("/sub-clientes", payload),
  update: (id, payload)=> api.put(`/sub-clientes/${id}`, payload),
  remove: (id)         => api.delete(`/sub-clientes/${id}`),
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

const useSubClientes = () => {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await subClienteService.list();
      setList(data);
    } catch {
      setError("Não foi possível carregar os clientes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { list, setList, loading, error, refetch: fetch };
};

// ─── Empty form ───────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  nome: "", cpf_cnpj: "", email: "", telefone: "", endereco: "", observacoes: "",
};

// ─── Icons ────────────────────────────────────────────────────────────────────

const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const UserIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const MailIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.58 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const DocIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);

const PinIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const NoteIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
  </svg>
);

// ─── FormField ────────────────────────────────────────────────────────────────

const FormField = memo(({ id, label, children, required, fullWidth }) => (
  <div className={`${styles.formGroup} ${fullWidth ? styles.fullWidth : ""}`}>
    <label htmlFor={id} className={styles.formLabel}>
      {label}
      {required && <span className={styles.required} aria-hidden="true"> *</span>}
    </label>
    {children}
  </div>
));
FormField.displayName = "FormField";

// ─── Modal ────────────────────────────────────────────────────────────────────

const SubClienteModal = memo(({ initial, onSave, onClose }) => {
  const isEdit = Boolean(initial?.id);
  const [form,   setForm]   = useState(initial ? {
    nome:        initial.nome        ?? "",
    cpf_cnpj:    initial.cpf_cnpj    ?? "",
    email:       initial.email       ?? "",
    telefone:    initial.telefone    ?? "",
    endereco:    initial.endereco    ?? "",
    observacoes: initial.observacoes ?? "",
  } : { ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const handleChange = useCallback(
    (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value })),
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 id="modal-title" className={styles.modalTitle}>
            {isEdit ? "Editar Cliente" : "Novo Cliente"}
          </h2>
          <button type="button" className={styles.btnClose} onClick={onClose}
            aria-label="Fechar" disabled={saving}>
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalForm} noValidate>
          <div className={styles.formGrid}>
            <FormField id="nome" label="Nome" required>
              <input id="nome" type="text" className={styles.input}
                value={form.nome} onChange={handleChange("nome")}
                placeholder="Nome completo ou razão social" required disabled={saving} />
            </FormField>

            <FormField id="cpf_cnpj" label="CPF / CNPJ">
              <input id="cpf_cnpj" type="text" className={styles.input}
                value={form.cpf_cnpj} onChange={handleChange("cpf_cnpj")}
                placeholder="000.000.000-00" disabled={saving} />
            </FormField>

            <FormField id="email" label="E-mail">
              <input id="email" type="email" className={styles.input}
                value={form.email} onChange={handleChange("email")}
                placeholder="contato@email.com" disabled={saving} />
            </FormField>

            <FormField id="telefone" label="Telefone">
              <input id="telefone" type="tel" className={styles.input}
                value={form.telefone} onChange={handleChange("telefone")}
                placeholder="(11) 90000-0000" disabled={saving} />
            </FormField>

            <FormField id="endereco" label="Endereço" fullWidth>
              <input id="endereco" type="text" className={styles.input}
                value={form.endereco} onChange={handleChange("endereco")}
                placeholder="Rua, número, bairro, cidade" disabled={saving} />
            </FormField>

            <FormField id="observacoes" label="Observações" fullWidth>
              <textarea id="observacoes" className={styles.textarea}
                value={form.observacoes} onChange={handleChange("observacoes")}
                placeholder="Informações adicionais sobre o cliente..."
                rows={3} disabled={saving} />
            </FormField>
          </div>

          <div className={styles.modalFooter}>
            <button type="button" className={styles.btnCancel}
              onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className={styles.btnSave}
              disabled={saving} aria-busy={saving}>
              {saving
                ? <><span className={styles.spinner} aria-hidden="true" /> Salvando...</>
                : isEdit ? "Salvar alterações" : "Cadastrar cliente"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});
SubClienteModal.displayName = "SubClienteModal";

// ─── Card de sub-cliente ──────────────────────────────────────────────────────

const SubClienteCard = memo(({ sc, onEdit, onDelete }) => {
  const initials = sc.nome
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  return (
    <div className={styles.clienteCard}>
      {/* Avatar */}
      <div className={styles.cardAvatar} aria-hidden="true">
        {initials || <UserIcon />}
      </div>

      {/* Info */}
      <div className={styles.cardBody}>
        <h3 className={styles.cardName}>{sc.nome}</h3>

        <div className={styles.cardMeta}>
          {sc.cpf_cnpj && (
            <span className={styles.metaItem}>
              <DocIcon /> {sc.cpf_cnpj}
            </span>
          )}
          {sc.email && (
            <span className={styles.metaItem}>
              <MailIcon /> {sc.email}
            </span>
          )}
          {sc.telefone && (
            <span className={styles.metaItem}>
              <PhoneIcon /> {sc.telefone}
            </span>
          )}
          {sc.endereco && (
            <span className={styles.metaItem}>
              <PinIcon /> {sc.endereco}
            </span>
          )}
          {sc.observacoes && (
            <span className={`${styles.metaItem} ${styles.metaObs}`}>
              <NoteIcon /> {sc.observacoes}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className={styles.cardActions}>
        <button type="button" className={styles.btnAction} onClick={() => onEdit(sc)}
          aria-label={`Editar ${sc.nome}`} title="Editar">
          <EditIcon />
        </button>
        <button type="button" className={`${styles.btnAction} ${styles.btnDanger}`}
          onClick={() => onDelete(sc)} aria-label={`Remover ${sc.nome}`} title="Remover">
          <TrashIcon />
        </button>
      </div>
    </div>
  );
});
SubClienteCard.displayName = "SubClienteCard";

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <div className={styles.skeletonCard} aria-hidden="true">
    <div className={styles.skeletonAvatar} />
    <div className={styles.skeletonLines}>
      <div className={styles.skeletonLine} style={{ width: "55%" }} />
      <div className={styles.skeletonLine} style={{ width: "75%" }} />
      <div className={styles.skeletonLine} style={{ width: "45%" }} />
    </div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const SubClientePage = () => {
  const { list, setList, loading, error, refetch } = useSubClientes();
  const [search,  setSearch]  = useState("");
  const [modal,   setModal]   = useState(null); // null | { mode: "create"|"edit", data?: {} }
  const searchRef = useRef(null);

  // ── Filtered list ───────────────────────────────────────────────────────────
  const filtered = list.filter((sc) => {
    const q = search.toLowerCase();
    return (
      sc.nome?.toLowerCase().includes(q) ||
      sc.email?.toLowerCase().includes(q) ||
      sc.cpf_cnpj?.toLowerCase().includes(q) ||
      sc.telefone?.toLowerCase().includes(q)
    );
  });

  // ── Save (create or edit) ───────────────────────────────────────────────────
  const handleSave = useCallback(async (form) => {
    try {
      if (modal?.data?.id) {
        // edit
        const { data } = await subClienteService.update(modal.data.id, form);
        setList((prev) => prev.map((sc) => sc.id === data.id ? data : sc));
        Swal.fire({ icon: "success", title: "Cliente atualizado!", timer: 1400, showConfirmButton: false });
      } else {
        // create
        const { data } = await subClienteService.create(form);
        setList((prev) => [...prev, data]);
        Swal.fire({ icon: "success", title: "Cliente cadastrado!", timer: 1400, showConfirmButton: false });
      }
      setModal(null);
    } catch {
      Swal.fire("Erro", "Não foi possível salvar o cliente.", "error");
    }
  }, [modal, setList]);

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (sc) => {
    const result = await Swal.fire({
      title: `Remover "${sc.nome}"?`,
      text: "Esta ação não poderá ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E53E3E",
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;

    try {
      await subClienteService.remove(sc.id);
      setList((prev) => prev.filter((c) => c.id !== sc.id));
      Swal.fire({ icon: "success", title: "Removido!", timer: 1200, showConfirmButton: false });
    } catch {
      Swal.fire("Erro", "Não foi possível remover.", "error");
    }
  }, [setList]);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className={styles.pageHeader}>
        <div className={styles.pageTitle}>
          <h1 className={styles.title}>Meus Clientes</h1>
          <p className={styles.subtitle}>
            Gerencie os clientes vinculados à sua conta
          </p>
        </div>
        <button
          type="button"
          className={styles.btnNew}
          onClick={() => setModal({ mode: "create" })}
        >
          <PlusIcon /> Novo cliente
        </button>
      </div>

      {/* ── Search + count ─────────────────────────────────────────────────── */}
      <div className={styles.toolbar}>
        <div className={styles.searchWrapper}>
          <span className={styles.searchIcon} aria-hidden="true"><SearchIcon /></span>
          <input
            ref={searchRef}
            type="search"
            className={styles.searchInput}
            placeholder="Buscar por nome, e-mail, CPF/CNPJ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar clientes"
          />
        </div>
        {!loading && !error && (
          <span className={styles.count}>
            {filtered.length} {filtered.length === 1 ? "cliente" : "clientes"}
          </span>
        )}
      </div>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      {loading ? (
        <div className={styles.grid}>
          {[1, 2, 3].map((n) => <SkeletonCard key={n} />)}
        </div>
      ) : error ? (
        <div className={styles.errorBox} role="alert">
          <p>{error}</p>
          <button className={styles.btnRetry} onClick={refetch}>Tentar novamente</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon} aria-hidden="true"><UserIcon /></div>
          <p className={styles.emptyTitle}>
            {search ? "Nenhum cliente encontrado" : "Nenhum cliente cadastrado"}
          </p>
          <p className={styles.emptyDesc}>
            {search
              ? "Tente buscar por outro termo."
              : "Clique em \"Novo cliente\" para começar."}
          </p>
          {!search && (
            <button className={styles.btnNew} onClick={() => setModal({ mode: "create" })}>
              <PlusIcon /> Novo cliente
            </button>
          )}
        </div>
      ) : (
        <div className={styles.grid}>
          {filtered.map((sc) => (
            <SubClienteCard
              key={sc.id}
              sc={sc}
              onEdit={(sc) => setModal({ mode: "edit", data: sc })}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* ── Modal ──────────────────────────────────────────────────────────── */}
      {modal && (
        <SubClienteModal
          initial={modal.data ?? null}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default SubClientePage;